import { useCallback, useEffect, useMemo, useState } from 'react';
import { INSTITUTION_CATEGORY_BADGE } from '../../../../constants/consumers';
// icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRotateRight, faCircleCheck, faWarning } from '@fortawesome/free-solid-svg-icons';
// API
import { useEnv } from '../../../../context/env-context';
import { useAuth } from '../../../../hooks/useAuth';
import { toast } from 'react-toastify';
import { makeRequest } from '../../../../utils/makeRequest';
// components
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import { Spinner } from '../../../../components/Spinner';
import CollapsedPanel from '../../../../components/CollapsedPanel';
import ConsumerAccordion from '../shared/ConsumerAccordion';
import ConsumerPopoverBankStatusForm from './forms/ConsumerPopoverBankStatusForm';
// widgets
import ConsumerBalancesWidget from './widgets/ConsumerBalancesWidget';
import ConsumerProfileWidget from './widgets/ConsumerProfileWidget';
// sections
import ConsumerBankAccountsSection from './sections/ConsumerBankAccountsSection';
import ConsumerAchDwollaSection from './sections/ConsumerAchDwollaSection';
import ConsumerKycSection from './sections/ConsumerKycSection';
import ConsumerAstraCardsSection from './sections/ConsumerAstraCardsSection';
import ConsumerTabaPayCardsSection from './sections/ConsumerTabaPayCardsSection';

function ConsumerBankPanel({
  collapsedPanels,
  expandParentPanel,
  consumer,
  setConsumer,
  getConsumer,
  refreshConsumer,
  loading: globalLoading,
  profileJson,
  panelDataRefresh,
  setPanelDataRefresh,
}) {
  const { getAccessTokenSilently } = useAuth();
  const { apiOriginConsumer, apiOriginFunding, apiOriginMonitor } = useEnv();

  const [spinner, setSpinner] = useState(false);
  const [loading, setLoading] = useState({ balances: false });

  const [balances, setBalances] = useState({ balances: [], date: null });
  const [institution, setInstitution] = useState(null);
  const [fundingSource, setFundingSource] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountOwners, setBankAccountOwners] = useState([]);
  const [astraUser, setAstraUser] = useState(null);
  const [cards, setCards] = useState([]);

  const [accountsBlockStatus, setAccountsBlockStatus] = useState({
    blocked: false,
    blockedUntil: null,
  });
  const [cardsBlockStatus, setCardsBlockStatus] = useState({
    blocked: false,
    blockedUntil: 'null',
  });

  const tabaPayCards = useMemo(() => cards?.filter(x => x.tpCardId) || [], [cards]);

  const getRequest = useCallback(
    async (url, setFunction) => {
      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      const config = { token, url, method: 'GET', params: { consumerId: consumer.id } };

      try {
        const response = await makeRequest(config);

        setFunction(response);
      } catch (error) {
        toast.error(error.message);
      }
    },
    [consumer?.id, getAccessTokenSilently],
  );

  const getInstitution = useCallback(
    () => getRequest(`${apiOriginConsumer}/getInstitution`, setInstitution),
    [apiOriginConsumer, getRequest],
  );

  const getFundingSource = useCallback(
    () => getRequest(`${apiOriginFunding}/get_funding_source`, setFundingSource),
    [apiOriginFunding, getRequest],
  );

  const getBankAccounts = useCallback(
    () => getRequest(`${apiOriginConsumer}/get_all_accounts`, setBankAccounts),
    [apiOriginConsumer, getRequest],
  );

  const getBankAccountOwners = useCallback(
    () => getRequest(`${apiOriginConsumer}/getOwners`, setBankAccountOwners),

    [apiOriginConsumer, getRequest],
  );

  const getAstraUser = useCallback(
    () => getRequest(`${apiOriginConsumer}/astra/user`, setAstraUser),
    [apiOriginConsumer, getRequest],
  );

  const getCards = useCallback(
    () => getRequest(`${apiOriginConsumer}/cards/get`, setCards),
    [apiOriginConsumer, getRequest],
  );

  const getBalances = useCallback(async () => {
    setLoading({ balances: true });
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginMonitor}/balances`,
      method: 'GET',
      params: { consumerId: consumer.id },
    };

    try {
      const response = await makeRequest(config);

      setBalances(response);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading({ balances: false });
    }
  }, [apiOriginMonitor, consumer?.id, getAccessTokenSilently]);

  const getBlockStatus = useCallback(
    async (url, setFunction) => {
      const token = await getAccessTokenSilently();

      if (!token) {
        setSpinner(false);

        return { blocked: false, blockedUntil: null };
      }

      const config = { url, token, method: 'GET', params: { consumerId: consumer.id } };

      try {
        const response = await makeRequest(config);

        setFunction(response);

        return response;
      } catch (error) {
        console.error(error);

        return { blocked: false, blockedUntil: null };
      }
    },
    [consumer?.id, getAccessTokenSilently],
  );

  const getAccountsBlockStatus = useCallback(
    () => getBlockStatus(`${apiOriginConsumer}/link/block_status`, setAccountsBlockStatus),
    [apiOriginConsumer, getBlockStatus],
  );

  const getCardsBlockStatus = useCallback(
    () => getBlockStatus(`${apiOriginConsumer}/cards/link_block_status`, setCardsBlockStatus),
    [apiOriginConsumer, getBlockStatus],
  );

  const refreshBalances = useCallback(async () => {
    setLoading({ balances: true });
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginMonitor}/get_balance`,
      method: 'GET',
      params: { consumerId: consumer.id },
    };

    try {
      const response = await makeRequest(config);

      if (response) {
        getBalances();
      }
    } catch (error) {
      setLoading({ balances: false });
      toast.error(error.message);
    }
  }, [apiOriginMonitor, consumer?.id, getAccessTokenSilently, getBalances]);

  useEffect(() => {
    if (panelDataRefresh.balances) {
      getBalances();
      setPanelDataRefresh(prev => ({ ...prev, balances: false }));
    }
  }, [getBalances, panelDataRefresh, setPanelDataRefresh]);

  const refreshBankData = useCallback(async () => {
    await Promise.all([
      getInstitution(),
      getFundingSource(),
      getBankAccounts(),
      getBankAccountOwners(),
      getAstraUser(),
      getCards(),
      getCardsBlockStatus(),
      getAccountsBlockStatus(),
    ]);
    getBalances();
  }, [
    getInstitution,
    getFundingSource,
    getBankAccounts,
    getBankAccountOwners,
    getAstraUser,
    getBalances,
    getCards,
    getCardsBlockStatus,
    getAccountsBlockStatus,
  ]);

  const refresh = async () => {
    setSpinner(true);
    await Promise.all([refreshBankData(consumer.id), refreshConsumer(consumer.id)]);
    setSpinner(false);
  };

  const inititializeData = async () => {
    setSpinner(true);
    await refreshBankData(consumer.id);
    setSpinner(false);
  };

  useEffect(() => {
    inititializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumer?.id]);

  const disconnectBankAccount = useCallback(async () => {
    if (window.confirm('Are you sure?')) {
      setSpinner(true);
      try {
        const token = await getAccessTokenSilently();

        if (!token) {
          return;
        }

        const config = {
          token,
          url: `${apiOriginConsumer}/link/disconnect`,
          method: 'POST',
          data: JSON.stringify({ consumerId: consumer.id }),
        };

        await makeRequest(config);
        await refreshBankData(consumer.id);

        setConsumer(prevConsumer => ({ ...prevConsumer, bankAccountStatus: 'NOT_CONNECTED' }));
        toast.success('Consumer successfully disconnected');
      } catch (error) {
        toast.error(error.message);
      } finally {
        setSpinner(false);
      }
    }
  }, [apiOriginConsumer, consumer.id, getAccessTokenSilently, setConsumer, refreshBankData]);

  const sendCardRequest = useCallback(
    async ({ url, data, askForConfirmation = false }) => {
      const confirmation = askForConfirmation ? window.confirm('Are you sure?') : true;

      if (confirmation) {
        setSpinner(true);

        const token = await getAccessTokenSilently();

        if (!token) {
          return;
        }

        const config = {
          token,
          url: `${apiOriginConsumer}/cards/${url}`,
          method: 'POST',
          data: JSON.stringify(data),
        };

        try {
          await makeRequest(config);
          await getAstraUser();
          await getCards();
        } catch (error) {
          toast.error(error.message);
        } finally {
          setSpinner(false);
        }
      }
    },
    [getAstraUser, getCards, getAccessTokenSilently, apiOriginConsumer],
  );

  const unblockLinkStatus = useCallback(async () => {
    setSpinner(true);

    const token = await getAccessTokenSilently();

    if (!token) {
      setSpinner(false);
    }

    const config = {
      url: `${apiOriginConsumer}/unblock_link_attempts`,
      token,
      method: 'POST',
      data: JSON.stringify({ consumerId: consumer?.id }),
    };

    try {
      await makeRequest(config);
      await Promise.allSettled([getAccountsBlockStatus(), getCardsBlockStatus()]);
    } catch (error) {
      console.error(error);
    } finally {
      setSpinner(false);
    }
  }, [
    apiOriginConsumer,
    consumer?.id,
    getAccessTokenSilently,
    getAccountsBlockStatus,
    getCardsBlockStatus,
  ]);

  const getBankAccountValue = useCallback(
    card => {
      if (card.linkedAccountAchToken) {
        const account = bankAccounts?.find(acc => acc.achToken === card.linkedAccountAchToken);

        if (account) {
          return (
            <>
              <FontAwesomeIcon
                icon={faCircleCheck}
                size="1x"
                color={account.isMain ? 'green' : 'orange'}
                className="me-1"
              />
              <p className="bo-consumer-text text-truncate">{account.name}</p>
            </>
          );
        }

        return (
          <Badge className="bo-consumer-inline-badge" bg="warning">
            Previously Linked Account
          </Badge>
        );
      }

      if (card.nonLinkedAaccountAchTokens?.length > 0) {
        const accounts = bankAccounts.filter(acc =>
          card.nonLinkedAaccountAchTokens.includes(acc.achToken),
        );

        if (accounts.length > 0) {
          return (
            <div className="text-truncate">
              {accounts.map(x => (
                <p key={x.plaidAccountId} className="bo-consumer-text text-danger">
                  <strong>Not </strong>
                  {x.name}
                </p>
              ))}
            </div>
          );
        }
      }

      return (
        <Badge className="bo-consumer-inline-badge" bg="warning">
          Unknown
        </Badge>
      );
    },
    [bankAccounts],
  );

  if (collapsedPanels.bank) {
    return <CollapsedPanel side="left" onClick={() => expandParentPanel(2)} />;
  }

  return (
    <Spinner visible={spinner}>
      <div className="h-100">
        <div className="py-2 ps-2 pe-3">
          <div className="d-flex align-items-center justify-content-between gap-2">
            <p className="bo-consumer-text-xl-bold pb-1 text-truncate">
              {institution?.name || '----'}
            </p>
            <div className="d-flex align-items-center">
              <ConsumerPopoverBankStatusForm
                consumerId={consumer.id}
                consumerBankAccountStatus={consumer.bankAccountStatus}
                consumerBankAccountStatusMessage={consumer.bankAccountStatusMessage}
                refreshConsumer={refreshConsumer}
              />
              <Button className="bo-common-base-button pb-1" onClick={refresh}>
                <FontAwesomeIcon icon={faArrowRotateRight} size="sm" color="#5946A4" />
              </Button>
            </div>
          </div>
          <div className="d-flex">
            <p
              className={`bo-consumer-text ${
                institution &&
                institution.category !== 'A' &&
                institution.category !== 'B' &&
                institution.category !== 'C' &&
                'text-danger'
              }`}
            >
              {INSTITUTION_CATEGORY_BADGE[institution?.category]
                ? `Category ${institution?.category}`
                : '----'}
            </p>
          </div>
        </div>
        <div className="py-2 ps-2 pe-3">
          <ConsumerProfileWidget
            loading={globalLoading.profileJson}
            profileJson={profileJson}
            consumerProfileStatus={consumer.profileStatus}
          />
        </div>
        {consumer.bankAccountStatus !== 'NOT_CONNECTED' &&
          consumer.bankAccountStatus !== 'NO_TRANSFERS' && (
            <div className="py-2 ps-2 pe-3">
              <ConsumerBalancesWidget
                loading={loading.balances}
                balances={balances}
                bankAccounts={bankAccounts}
                refreshBalances={refreshBalances}
                consumerIsTest={consumer.isTest}
              />
            </div>
          )}
        <ConsumerAccordion title="Bank Accounts (Plaid)">
          <ConsumerBankAccountsSection
            consumerId={consumer.id}
            consumerPlaidItemId={consumer.plaidItemId}
            consumerBankAccountStatus={consumer.bankAccountStatus}
            disconnectBankAccount={disconnectBankAccount}
            institution={institution}
            bankAccounts={bankAccounts}
            setBankAccounts={setBankAccounts}
            getBankAccounts={getBankAccounts}
            bankAccountOwners={bankAccountOwners}
            getFundingSource={getFundingSource}
            accountsBlockStatus={accountsBlockStatus}
            unblockLinkStatus={unblockLinkStatus}
          />
        </ConsumerAccordion>
        <ConsumerAccordion
          title={`Cards (Astra) ${
            astraUser?.cards?.length > 0 ? `(${astraUser?.cards?.length})` : ''
          }`}
          renderLeftIcon={
            cards?.length > 0 && cards.find(card => card.isMain)?.status !== 'OK'
              ? () => <FontAwesomeIcon icon={faWarning} size="1x" color="red" />
              : null
          }
        >
          <ConsumerAstraCardsSection
            astraUser={astraUser}
            setSpinner={setSpinner}
            getCards={getCards}
            getAstraUser={getAstraUser}
            sendCardRequest={sendCardRequest}
            getBankAccountValue={getBankAccountValue}
            cardsBlockStatus={cardsBlockStatus}
            getCardsBlockStatus={getCardsBlockStatus}
            unblockLinkStatus={unblockLinkStatus}
            consumerId={consumer.id}
            consumerFirstName={consumer.firstName}
            consumerLastName={consumer.lastName}
            consumerAddressLine1={consumer.addressLine1}
            consumerAddressLine2={consumer.addressLine2}
            consumerCity={consumer.city}
            consumerState={consumer.state}
            consumerZip={consumer.zip}
          />
        </ConsumerAccordion>
        <ConsumerAccordion
          title={`Cards (TabaPay) ${tabaPayCards.length > 0 ? `(${tabaPayCards.length})` : ''}`}
          renderLeftIcon={
            cards?.length > 0 && cards.find(card => card.isMain)?.status !== 'OK'
              ? () => <FontAwesomeIcon icon={faWarning} size="1x" color="red" />
              : null
          }
        >
          <ConsumerTabaPayCardsSection
            tabaPayCards={tabaPayCards}
            getCards={getCards}
            getAstraUser={getAstraUser}
            sendCardRequest={sendCardRequest}
            getBankAccountValue={getBankAccountValue}
            cardsBlockStatus={cardsBlockStatus}
            getCardsBlockStatus={getCardsBlockStatus}
            unblockLinkStatus={unblockLinkStatus}
            consumerId={consumer.id}
            consumerFirstName={consumer.firstName}
            consumerLastName={consumer.lastName}
            consumerAddressLine1={consumer.addressLine1}
            consumerAddressLine2={consumer.addressLine2}
            consumerCity={consumer.city}
            consumerState={consumer.state}
            consumerZip={consumer.zip}
            consumerManualCardValidation={consumer.manualCardValidation}
            consumerAllowAdHocCardException={consumer.allowAdHocCardException}
            setConsumer={setConsumer}
          />
        </ConsumerAccordion>
        <ConsumerAccordion
          title="KYC"
          renderLeftIcon={
            consumer.idvKycStatus !== 'APPROVED' ||
            consumer.idvWlsStatus !== 'APPROVED' ||
            consumer.idvRiskStatus !== 'APPROVED'
              ? () => <FontAwesomeIcon icon={faWarning} size="1x" color="red" />
              : null
          }
        >
          <ConsumerKycSection
            consumerId={consumer.id}
            consumerIdvKycStatus={consumer.idvKycStatus}
            consumerIdvWlsStatus={consumer.idvWlsStatus}
            consumerIdvRiskStatus={consumer.idvRiskStatus}
            consumerLastIdv={consumer.lastIdv}
            consumerLastWls={consumer.lastWls}
            consumerIdvMessage={consumer.idvMessage}
            setPanelDataRefresh={setPanelDataRefresh}
            getConsumer={getConsumer}
          />
        </ConsumerAccordion>
        <ConsumerAccordion title="ACH (Dwolla)" bottomBorder>
          <ConsumerAchDwollaSection
            consumerId={consumer.id}
            consumerDwollaCustomerUrl={consumer.dwollaCustomerUrl}
            fundingSource={fundingSource}
            setSpinner={setSpinner}
          />
        </ConsumerAccordion>
      </div>
    </Spinner>
  );
}

export default ConsumerBankPanel;
